import countries from "./countries.json"

/** A city suggestion shown in the birthplace combobox. */
export interface CityRecord {
  /** "City, Country" — the value stored in answers and shown in the input. */
  label: string
  /** City display name (keeps diacritics). */
  city: string
  /** Country display name. */
  country: string
  /** ISO-3166 alpha-2 country code (drives the flag emoji). */
  cc: string
  /** Admin-1 region name shown on the muted second line; may be empty. */
  region: string
  /** WGS84 from GeoNames; omitted for the handful of rows we couldn't match. */
  lat?: number
  lon?: number
  /** Stable id so equality survives re-filtered result lists. */
  id: string
}

/** Compact dataset row: [name, asciiSearchKey, countryCode, region, lat?, lon?]. */
type Row = [string, string, string, string, number?, number?]

const countryNames = countries as Record<string, string>

let cache: Row[] | null = null
let loading: Promise<Row[]> | null = null

/**
 * Lazily load the (~2.6MB) GeoNames city dataset as its own chunk on first use,
 * so it never weighs on the initial bundle. Generated by `scripts/generate-cities.mjs`.
 */
export function loadCities(): Promise<Row[]> {
  if (cache) return Promise.resolve(cache)
  if (!loading) {
    loading = import("./cities.json").then((m) => {
      cache = m.default as Row[]
      return cache
    })
  }
  return loading
}

const normalize = (s: string) =>
  s.trim().toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "")

const flagOf = (cc: string) =>
  cc.length === 2
    ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)))
    : ""

function toRecord(row: Row, index: number): CityRecord {
  const country = countryNames[row[2]] ?? row[2]
  const lat = row[4]
  const lon = row[5]
  return {
    city: row[0],
    country,
    cc: row[2],
    region: row[3],
    label: `${row[0]}, ${country}`,
    id: String(index),
    ...(typeof lat === "number" && typeof lon === "number" ? { lat, lon } : {}),
  }
}

/**
 * Cities the world renamed, keyed by the old name people still type. GeoNames
 * only carries the current name, and these are too far from it for typo
 * tolerance to bridge ("peking" → "beijing" is three edits out of seven), so
 * they need to be stated. Deliberately a short hand-kept list of well-known
 * renames rather than the full GeoNames alternate-name dump, which costs
 * hundreds of kilobytes to cover names nobody types.
 */
const RENAMED: Record<string, string> = {
  bombay: "mumbai",
  calcutta: "kolkata",
  madras: "chennai",
  peking: "beijing",
  saigon: "ho chi minh city",
  leningrad: "saint petersburg",
  stalingrad: "volgograd",
  danzig: "gdansk",
  breslau: "wroclaw",
  konigsberg: "kaliningrad",
  bangalore: "bengaluru",
  rangoon: "yangon",
  constantinople: "istanbul",
}

/**
 * Distance from `q` to the closest PREFIX of `text`, capped at `max` (returns
 * `max + 1` once it cannot come in under the cap). Prefix-relative because the
 * user is mid-typing: "new you" should be 1 away from "new york", not 1 away
 * only after the whole word is spelled out. Standard banded edit-distance DP,
 * taking the row minimum so any prefix of `text` may be the match.
 */
function prefixDistance(q: string, text: string, max: number): number {
  const n = q.length
  const m = Math.min(text.length, n + max)
  let prev = new Array<number>(n + 1)
  let cur = new Array<number>(n + 1)
  for (let i = 0; i <= n; i++) prev[i] = i
  let best = prev[n]
  for (let j = 1; j <= m; j++) {
    cur[0] = 0 // a prefix may start matching anywhere → deletions from `text` are free at the front
    let rowMin = cur[0]
    const cj = text.charCodeAt(j - 1)
    for (let i = 1; i <= n; i++) {
      const cost = q.charCodeAt(i - 1) === cj ? 0 : 1
      const v = Math.min(prev[i] + 1, cur[i - 1] + 1, prev[i - 1] + cost)
      cur[i] = v
      if (v < rowMin) rowMin = v
    }
    if (rowMin > max) return max + 1 // whole row over budget — no prefix can recover
    if (cur[n] < best) best = cur[n]
    const swap = prev
    prev = cur
    cur = swap
  }
  return best
}

/** Edit budget for a query: short ones must be near-exact, longer ones get slack. */
function typoBudget(len: number): number {
  if (len < 4) return 0
  if (len < 7) return 1
  return 2
}

/**
 * Top matches for a query. Tiers, best first: name prefix, name substring,
 * region prefix (GeoNames names cities by their local romanization but keeps
 * the familiar regional name — "Mogilev" is the region of "Mahilyow"), then a
 * typo-tolerant pass so a misspelling still lands ("New You" → New York).
 *
 * The fuzzy pass only runs when the exact tiers come up short, since it has to
 * score every row; the exact tiers cover the overwhelmingly common case and bail
 * out early. The dataset is pre-sorted by population, so the biggest cities
 * surface first within each tier. Diacritics are folded on both sides.
 */
export function searchCities(rows: Row[], query: string, limit = 8): CityRecord[] {
  const raw = normalize(query)
  if (raw.length < 2) return []
  // A query that spells a former name searches for the current one instead.
  const renamedTo = Object.keys(RENAMED).find((old) => old.startsWith(raw) && raw.length >= 3)
  const q = renamedTo ? RENAMED[renamedTo] : raw

  const prefix: CityRecord[] = []
  const contains: CityRecord[] = []
  const byRegion: CityRecord[] = []

  for (let i = 0; i < rows.length; i++) {
    const key = rows[i][1] || rows[i][0].toLowerCase()
    const at = key.indexOf(q)
    if (at === 0) {
      prefix.push(toRecord(rows[i], i))
      if (prefix.length >= limit) break
      continue
    }
    if (at > 0) {
      if (contains.length < limit) contains.push(toRecord(rows[i], i))
      continue
    }
    if (byRegion.length < limit && normalize(rows[i][3]).startsWith(q)) {
      byRegion.push(toRecord(rows[i], i))
    }
  }

  let out = prefix.length >= limit ? prefix : [...prefix, ...contains, ...byRegion]
  if (out.length >= limit) return out.slice(0, limit)

  const budget = typoBudget(q.length)
  if (budget > 0) {
    const seen = new Set(out.map((r) => r.id))
    const scored: { record: CityRecord; distance: number }[] = []
    for (let i = 0; i < rows.length; i++) {
      if (seen.has(String(i))) continue
      const key = rows[i][1] || rows[i][0].toLowerCase()
      // Cheap gate before the DP: a candidate too short can never be within budget.
      if (key.length + budget < q.length) continue
      const distance = prefixDistance(q, key, budget)
      if (distance <= budget) scored.push({ record: toRecord(rows[i], i), distance })
    }
    // Fewest edits first; population order (row index) breaks ties.
    scored.sort((a, b) => a.distance - b.distance)
    out = [...out, ...scored.map((s) => s.record)]
  }
  return out.slice(0, limit)
}

export { flagOf }
